import { useState, useEffect } from "react";
import "@/App.css";
import axios from "axios";
import { 
  Home, 
  Hammer, 
  ShieldCheck, 
  Clock, 
  Phone, 
  MapPin, 
  CheckCircle2, 
  Star, 
  ChevronRight, 
  Wrench, 
  Droplets,
  Thermometer,
  AlertTriangle,
  Menu,
  X,
  Send,
  Trees,
  Sparkles,
  Construction
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// French Flag Colors
const COLORS = {
  bleu: "#002654",
  blanc: "#FFFFFF",
  rouge: "#CE1126",
  bleuClair: "#1e3a5f"
};

// Services data
const services = [
  {
    id: "renovation",
    title: "Rénovation de Toiture",
    description: "Remise à neuf complète de votre couverture avec des matériaux de qualité.",
    icon: Home,
    image: "https://images.unsplash.com/photo-1725656803766-103a8ccd8e1f?w=600&q=80"
  },
  {
    id: "reparation",
    title: "Réparation & Dépannage",
    description: "Intervention rapide pour tous vos problèmes de toiture : fuites, tuiles cassées.",
    icon: Wrench,
    image: "https://images.unsplash.com/photo-1666156333173-30cc51891948?w=600&q=80"
  },
  {
    id: "charpente",
    title: "Charpente",
    description: "Création, rénovation et renforcement de charpente traditionnelle ou industrielle.",
    icon: Hammer,
    image: null
  },
  {
    id: "zinguerie",
    title: "Zinguerie & Gouttières",
    description: "Pose et réparation de gouttières, chéneaux et évacuation des eaux pluviales.",
    icon: Droplets,
    image: null
  },
  {
    id: "demoussage",
    title: "Démoussage & Nettoyage",
    description: "Nettoyage, démoussage et traitement hydrofuge pour prolonger la vie de votre toit.",
    icon: Sparkles,
    image: null
  },
  {
    id: "isolation",
    title: "Isolation de Toiture",
    description: "Améliorez votre confort et réduisez vos factures d'énergie.",
    icon: Thermometer,
    image: "https://images.unsplash.com/photo-1681049400158-0ff6249ac315?w=600&q=80"
  },
  {
    id: "traitement-bois",
    title: "Traitement des Bois",
    description: "Protection contre les insectes xylophages, champignons et humidité (traitement curatif et préventif).",
    icon: Trees,
    image: null
  },
  {
    id: "maconnerie",
    title: "Maçonnerie",
    description: "Travaux de maçonnerie liés à la toiture : souche de cheminée, enduit, scellements.",
    icon: Construction,
    image: null
  },
  {
    id: "urgence",
    title: "Urgence 7j/7",
    description: "Intervention en urgence pour dégâts des intempéries ou accidents.",
    icon: AlertTriangle,
    image: null
  }
];

const benefits = [
  { icon: ShieldCheck, title: "Garantie 10 ans", description: "Tous nos travaux sont garantis" },
  { icon: Clock, title: "Devis sous 48h", description: "Réponse rapide à votre demande" },
  { icon: Star, title: "Artisan qualifié", description: "Plus de 15 ans d'expérience" },
  { icon: MapPin, title: "Intervention locale", description: "Nous intervenons dans votre région" }
];

// Photos de réalisations
const realisations = [
  {
    id: 1,
    title: "Rénovation toiture tuiles",
    category: "Tuiles",
    image: "https://customer-assets.emergentagent.com/job_build-website-77/artifacts/e5vo0nln_IMG_0380.jpeg"
  },
  {
    id: 2,
    title: "Chantier en cours - Isolation",
    category: "Charpente",
    image: "https://customer-assets.emergentagent.com/job_build-website-77/artifacts/is9x04ka_IMG_5685.png"
  },
  {
    id: 3,
    title: "Bardage zinc et toiture finie",
    category: "Zinc",
    image: "https://customer-assets.emergentagent.com/job_build-website-77/artifacts/vwqk13o4_IMG_5686.png"
  },
  {
    id: 4,
    title: "Cheminée refaite à neuf - entourage zinc",
    category: "Zinc",
    location: "Butte Sainte-Anne, Chantenay - Nantes",
    image: "/realisations/chantier-cheminee-ardoise-3.webp"
  },
  {
    id: 5,
    title: "Couvertine et habillage zinc de cheminée",
    category: "Zinc",
    location: "Butte Sainte-Anne, Chantenay - Nantes",
    image: "/realisations/chantier-cheminee-ardoise-4.webp"
  }
];

const avantApres = {
  title: "Réfection de cheminée à neuf, support et entourage zinc",
  location: "Butte Sainte-Anne, Chantenay - Nantes (44000)",
  avant: "/realisations/chantier-cheminee-ardoise-1.webp",
  apres: "/realisations/chantier-cheminee-ardoise-2.webp"
};

const AvantApresPhoto = ({ src, label, color, testId, onOpen }) => (
  <div className="relative aspect-[3/4] rounded-xl overflow-hidden shadow-md cursor-pointer group" onClick={onOpen} data-testid={testId}>
    <img src={src} alt={`${label} - ${avantApres.title}`} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
    <span className="absolute top-3 left-3 px-3 py-1 text-xs sm:text-sm font-bold text-white uppercase rounded-full" style={{ backgroundColor: color }}>{label}</span>
  </div>
);

const AvantApresBlock = ({ onOpen }) => (
  <div className="mb-12" data-testid="avant-apres-block">
    <div className="grid grid-cols-2 gap-3 sm:gap-4 max-w-3xl mx-auto">
      <AvantApresPhoto src={avantApres.avant} label="Avant" color={COLORS.bleu} testId="avant-photo" onOpen={() => onOpen({ image: avantApres.avant, title: avantApres.title, category: "Avant", location: avantApres.location })} />
      <AvantApresPhoto src={avantApres.apres} label="Après" color={COLORS.rouge} testId="apres-photo" onOpen={() => onOpen({ image: avantApres.apres, title: avantApres.title, category: "Après", location: avantApres.location })} />
    </div>
    <div className="text-center mt-4">
      <h3 className="font-heading text-lg sm:text-xl font-bold uppercase" style={{ color: COLORS.bleu }} data-testid="avant-apres-title">{avantApres.title}</h3>
      <p className="text-sm text-slate-600 flex items-center justify-center gap-1" data-testid="avant-apres-location"><MapPin className="w-4 h-4" style={{ color: COLORS.rouge }} />{avantApres.location}</p>
    </div>
  </div>
);

const testimonials = [
  { name: "Marie D.", location: "Nantes", text: "Travail impeccable, équipe ponctuelle et professionnelle. Ma toiture est comme neuve !", rating: 5 },
  { name: "Pierre L.", location: "Rennes", text: "Intervention rapide suite à une fuite. Prix correct et travail soigné.", rating: 5 },
  { name: "Sophie M.", location: "Angers", text: "Excellent conseil pour le choix des matériaux. Je recommande vivement !", rating: 5 }
];

// Logo Component - Logo professionnel
const Logo = ({ className = "", textColor = "text-slate-900" }) => (
  <div className={`flex items-center gap-5 ${className}`}>
    <img 
      src="https://customer-assets.emergentagent.com/job_build-website-77/artifacts/o6lgmf0g_IMG_1599.png" 
      alt="Couvertures Françaises de l'Ouest"
      className="h-40 sm:h-44 w-auto object-contain"
    />
    <div className="flex flex-col">
      <span className="font-heading text-xs font-semibold tracking-wider" style={{ color: COLORS.bleu }}>
        SASU
      </span>
      <span className="font-heading text-2xl sm:text-3xl font-bold uppercase tracking-tight leading-tight">
        <span style={{ color: COLORS.bleu }}>Couvertures </span>
        <span style={{ color: COLORS.rouge }}>Françaises</span>
      </span>
      <span className="font-heading text-lg sm:text-xl font-semibold uppercase tracking-wider" style={{ color: COLORS.bleu }}>
        de l'Ouest
      </span>
    </div>
  </div>
);

// Tricolor Bar Component
const TricolorBar = () => (
  <div className="h-1 w-full flex">
    <div className="flex-1" style={{ backgroundColor: COLORS.bleu }}></div>
    <div className="flex-1" style={{ backgroundColor: COLORS.blanc }}></div>
    <div className="flex-1" style={{ backgroundColor: COLORS.rouge }}></div>
  </div>
);

// Navigation Component
const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  
  return (
    <>
      <TricolorBar />
      <nav className="fixed top-1 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm border-b border-slate-200" data-testid="navigation">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center min-h-[12rem] py-4">
            <div className="flex flex-col items-start gap-1">
              <a href="#" className="flex items-center" data-testid="logo">
                <Logo textColor="text-[#002654]" />
              </a>
              {/* Numéros de téléphone sous le nom de l'entreprise - alignés et bien lisibles */}
              <div className="flex flex-wrap items-center gap-x-5 gap-y-1 mt-2 text-sm sm:text-base font-bold" data-testid="phone-group">
                <a href="tel:0219271334" className="flex items-center gap-1.5 hover:opacity-80 transition-opacity whitespace-nowrap" style={{ color: COLORS.bleu }} data-testid="header-tel-bureau">
                  <Phone className="w-4 h-4" />
                  <span>02 19 27 13 34</span>
                </a>
                <a href="tel:0756253169" className="flex items-center gap-1.5 hover:opacity-80 transition-opacity whitespace-nowrap" style={{ color: COLORS.rouge }} data-testid="header-tel-chantier">
                  <Phone className="w-4 h-4" />
                  <span>07 56 25 31 69</span>
                </a>
              </div>
            </div>
          
            <div className="hidden md:flex items-center gap-8">
              <a href="#services" className="text-[#002654] hover:text-[#CE1126] transition-colors font-medium" data-testid="nav-services">Services</a>
              <a href="#realisations" className="text-[#002654] hover:text-[#CE1126] transition-colors font-medium" data-testid="nav-realisations">Réalisations</a>
              <a href="#avantages" className="text-[#002654] hover:text-[#CE1126] transition-colors font-medium" data-testid="nav-avantages">Avantages</a>
              <a href="#temoignages" className="text-[#002654] hover:text-[#CE1126] transition-colors font-medium" data-testid="nav-temoignages">Témoignages</a>
              <a href="/blog.html" className="text-[#002654] hover:text-[#CE1126] transition-colors font-medium" data-testid="nav-blog">Blog</a>
              <a href="#contact" className="btn-primary" data-testid="nav-contact-btn">
                Devis Gratuit
              </a>
            </div>
          
            <button 
              className="md:hidden p-2 text-[#002654]"
              onClick={() => setIsOpen(!isOpen)}
              data-testid="mobile-menu-btn"
            >
              {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      
        {/* Mobile menu */}
        {isOpen && (
          <div className="md:hidden bg-white border-t border-slate-200 py-4" data-testid="mobile-menu">
            <div className="px-4 flex flex-col gap-4">
              <a href="#services" className="text-[#002654] py-2 font-medium" onClick={() => setIsOpen(false)}>Services</a>
              <a href="#realisations" className="text-[#002654] py-2 font-medium" onClick={() => setIsOpen(false)}>Réalisations</a>
              <a href="#avantages" className="text-[#002654] py-2 font-medium" onClick={() => setIsOpen(false)}>Avantages</a>
              <a href="#temoignages" className="text-[#002654] py-2 font-medium" onClick={() => setIsOpen(false)}>Témoignages</a>
              <a href="/blog.html" className="text-[#002654] py-2 font-medium" onClick={() => setIsOpen(false)}>Blog</a>
              <a href="#contact" className="btn-primary text-center" onClick={() => setIsOpen(false)}>Devis Gratuit</a>
            </div>
          </div>
        )}
      </nav>
    </>
  );
};

// Hero Section
const HeroSection = () => {
  return (
    <section className="relative min-h-screen flex items-center pt-56" data-testid="hero-section">
      <div className="absolute inset-0 z-0">
        <img 
          src="https://images.unsplash.com/photo-1756016865217-bac7c13c3238?w=1920&q=80" 
          alt="Toiture" 
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0" style={{ background: `linear-gradient(to right, ${COLORS.bleu}ee, ${COLORS.bleu}aa, transparent)` }}></div>
      </div>
      
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="max-w-2xl">
          <div className="inline-flex items-center gap-2 bg-white/10 border border-white/20 rounded-full px-4 py-2 mb-6 backdrop-blur-sm">
            <ShieldCheck className="w-4 h-4 text-white" />
            <span className="text-sm text-white font-medium">Artisan certifié • Garantie décennale</span>
          </div>
          
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-white uppercase tracking-tight mb-4" data-testid="hero-title">
            Votre Couvreur<br />
            <span style={{ color: COLORS.rouge }}>à Nantes & dans l'Ouest</span>
          </h1>
          
          <p className="font-heading text-3xl sm:text-4xl mb-6" style={{ color: COLORS.rouge }}>
            "Une tuile ? On est là !"
          </p>
          
          <p className="text-lg text-blue-100 mb-8 leading-relaxed">
            Couverture, charpente, zinguerie, isolation, démoussage, traitement des bois et maçonnerie. 
            Artisan couvreur basé à Nantes. Devis gratuit et intervention rapide à Nantes, en Loire-Atlantique et dans tout l'Ouest de la France.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4">
            <a href="#contact" className="btn-primary text-lg px-8 py-4" data-testid="hero-cta-primary">
              <Phone className="w-5 h-5" />
              Demander un Devis Gratuit
            </a>
            <a href="#services" className="btn-secondary text-lg px-8 py-4" data-testid="hero-cta-secondary">
              Nos Services
              <ChevronRight className="w-5 h-5" />
            </a>
          </div>
          
          <div className="flex items-center gap-8 mt-12 pt-8 border-t border-white/20">
            <div>
              <div className="text-3xl font-bold text-white">15+</div>
              <div className="text-sm text-blue-200">Années d'expérience</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-white">500+</div>
              <div className="text-sm text-blue-200">Toitures réalisées</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-white">100%</div>
              <div className="text-sm text-blue-200">Clients satisfaits</div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Guarantee Badge */}
      <div className="hidden lg:block absolute right-12 top-1/2 -translate-y-1/2 z-10" data-testid="guarantee-badge">
        <div className="bg-white rounded-2xl shadow-2xl p-6 text-center transform rotate-3 hover:rotate-0 transition-transform border-t-4" style={{ borderTopColor: COLORS.rouge }}>
          <ShieldCheck className="w-12 h-12 mx-auto mb-2" style={{ color: COLORS.bleu }} />
          <div className="font-heading text-2xl font-bold" style={{ color: COLORS.bleu }}>GARANTIE</div>
          <div className="font-heading text-4xl font-bold" style={{ color: COLORS.rouge }}>10 ANS</div>
          <div className="text-sm text-slate-500 mt-1">Sur tous nos travaux</div>
        </div>
      </div>
    </section>
  );
};

// Services Section
const ServicesSection = () => {
  return (
    <section id="services" className="py-20 lg:py-32 bg-slate-50" data-testid="services-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold uppercase tracking-tight mb-4" style={{ color: COLORS.bleu }} data-testid="services-title">
            Nos Services
          </h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Une expertise complète pour tous vos besoins en couverture et toiture
          </p>
          <div className="w-24 h-1 mx-auto mt-6 flex">
            <div className="flex-1" style={{ backgroundColor: COLORS.bleu }}></div>
            <div className="flex-1" style={{ backgroundColor: COLORS.blanc, border: '1px solid #e2e8f0' }}></div>
            <div className="flex-1" style={{ backgroundColor: COLORS.rouge }}></div>
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((service, index) => (
            <div 
              key={service.id}
              className={`group relative bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 border-b-4 ${
                index === 0 ? 'lg:col-span-2 lg:row-span-2' : ''
              }`}
              style={{ borderBottomColor: index % 2 === 0 ? COLORS.bleu : COLORS.rouge }}
              data-testid={`service-card-${service.id}`}
            >
              {service.image ? (
                <div className="relative h-full min-h-[300px]">
                  <img 
                    src={service.image} 
                    alt={service.title}
                    className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0" style={{ background: `linear-gradient(to top, ${COLORS.bleu}ee, ${COLORS.bleu}66, transparent)` }}></div>
                  <div className="absolute bottom-0 left-0 right-0 p-6">
                    <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4" style={{ backgroundColor: COLORS.rouge }}>
                      <service.icon className="w-6 h-6 text-white" />
                    </div>
                    <h3 className="font-heading text-xl font-bold text-white uppercase mb-2">{service.title}</h3>
                    <p className="text-blue-100">{service.description}</p>
                  </div>
                </div>
              ) : (
                <div className="p-6 h-full flex flex-col justify-between min-h-[200px]">
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4 transition-colors" style={{ backgroundColor: `${COLORS.bleu}15` }}>
                    <service.icon className="w-6 h-6" style={{ color: COLORS.bleu }} />
                  </div>
                  <div>
                    <h3 className="font-heading text-xl font-bold uppercase mb-2" style={{ color: COLORS.bleu }}>{service.title}</h3>
                    <p className="text-slate-600">{service.description}</p>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Realisations Section (Gallery)
const RealisationsSection = () => {
  const [selectedImage, setSelectedImage] = useState(null);

  return (
    <section id="realisations" className="py-20 lg:py-32 bg-white" data-testid="realisations-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold uppercase tracking-tight mb-4" style={{ color: COLORS.bleu }} data-testid="realisations-title">
            Nos Réalisations
          </h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Découvrez quelques-uns de nos chantiers réalisés dans l'Ouest
          </p>
          <div className="w-24 h-1 mx-auto mt-6 flex">
            <div className="flex-1" style={{ backgroundColor: COLORS.bleu }}></div>
            <div className="flex-1" style={{ backgroundColor: COLORS.blanc, border: '1px solid #e2e8f0' }}></div>
            <div className="flex-1" style={{ backgroundColor: COLORS.rouge }}></div>
          </div>
        </div>
        
        <AvantApresBlock onOpen={setSelectedImage} />

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {realisations.map((realisation) => (
            <div 
              key={realisation.id}
              className="group relative aspect-square rounded-xl overflow-hidden cursor-pointer shadow-md hover:shadow-xl transition-all duration-300"
              onClick={() => setSelectedImage(realisation)}
              data-testid={`realisation-${realisation.id}`}
            >
              <img 
                src={realisation.image} 
                alt={realisation.title}
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <div className="absolute bottom-0 left-0 right-0 p-4">
                  <span className="inline-block px-2 py-1 text-xs font-semibold text-white rounded-full mb-2" style={{ backgroundColor: COLORS.rouge }}>
                    {realisation.category}
                  </span>
                  <h3 className="font-heading text-sm font-bold text-white uppercase">{realisation.title}</h3>
                  {realisation.location && <p className="text-xs text-white/80">{realisation.location}</p>}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Modal pour agrandir l'image */}
      {selectedImage && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 p-4"
          onClick={() => setSelectedImage(null)}
          data-testid="image-modal"
        >
          <button 
            className="absolute top-4 right-4 text-white hover:text-gray-300 transition-colors"
            onClick={() => setSelectedImage(null)}
            data-testid="close-modal-btn"
          >
            <X className="w-8 h-8" />
          </button>
          <div className="max-w-4xl max-h-[90vh] relative">
            <img 
              src={selectedImage.image} 
              alt={selectedImage.title}
              className="max-w-full max-h-[85vh] object-contain rounded-lg"
            />
            <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/80 to-transparent rounded-b-lg">
              <span className="inline-block px-3 py-1 text-sm font-semibold text-white rounded-full mb-2" style={{ backgroundColor: COLORS.rouge }}>
                {selectedImage.category}
              </span>
              <h3 className="font-heading text-xl font-bold text-white uppercase">{selectedImage.title}</h3>
              {selectedImage.location && <p className="text-sm text-white/80" data-testid="modal-location">{selectedImage.location}</p>}
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

// Benefits Section
const BenefitsSection = () => {
  return (
    <section id="avantages" className="py-20 lg:py-32 bg-white" data-testid="benefits-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold uppercase tracking-tight mb-4" style={{ color: COLORS.bleu }} data-testid="benefits-title">
            Pourquoi Nous Choisir
          </h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Un artisan de confiance pour des travaux de qualité
          </p>
          <div className="w-24 h-1 mx-auto mt-6 flex">
            <div className="flex-1" style={{ backgroundColor: COLORS.bleu }}></div>
            <div className="flex-1" style={{ backgroundColor: COLORS.blanc, border: '1px solid #e2e8f0' }}></div>
            <div className="flex-1" style={{ backgroundColor: COLORS.rouge }}></div>
          </div>
        </div>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
          {benefits.map((benefit, index) => (
            <div 
              key={index}
              className="text-center group"
              data-testid={`benefit-card-${index}`}
            >
              <div 
                className="w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-6 transition-all duration-300 group-hover:scale-110"
                style={{ 
                  backgroundColor: index % 2 === 0 ? `${COLORS.bleu}15` : `${COLORS.rouge}15`,
                }}
              >
                <benefit.icon 
                  className="w-10 h-10 transition-colors duration-300" 
                  style={{ color: index % 2 === 0 ? COLORS.bleu : COLORS.rouge }}
                />
              </div>
              <h3 className="font-heading text-xl font-bold uppercase mb-2" style={{ color: COLORS.bleu }}>{benefit.title}</h3>
              <p className="text-slate-600">{benefit.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Testimonials Section
const TestimonialsSection = () => {
  return (
    <section id="temoignages" className="py-20 lg:py-32" style={{ backgroundColor: COLORS.bleu }} data-testid="testimonials-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-white uppercase tracking-tight mb-4" data-testid="testimonials-title">
            Ils Nous Font Confiance
          </h2>
          <p className="text-lg text-blue-200 max-w-2xl mx-auto">
            L'avis de nos clients satisfaits dans l'Ouest
          </p>
          <div className="w-24 h-1 mx-auto mt-6 flex">
            <div className="flex-1 bg-white/30"></div>
            <div className="flex-1 bg-white"></div>
            <div className="flex-1" style={{ backgroundColor: COLORS.rouge }}></div>
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {testimonials.map((testimonial, index) => (
            <div 
              key={index}
              className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 relative border border-white/20"
              data-testid={`testimonial-card-${index}`}
            >
              <div className="flex gap-1 mb-4">
                {[...Array(testimonial.rating)].map((_, i) => (
                  <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                ))}
              </div>
              <p className="text-blue-100 mb-6 italic">"{testimonial.text}"</p>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ backgroundColor: `${COLORS.rouge}30` }}>
                  <span className="font-bold" style={{ color: COLORS.rouge }}>{testimonial.name.charAt(0)}</span>
                </div>
                <div>
                  <div className="font-semibold text-white">{testimonial.name}</div>
                  <div className="text-sm text-blue-300">{testimonial.location}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Contact Section
const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    address: "",
    service_type: "",
    message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const response = await axios.post(`${API}/contact`, formData);
      setSubmitStatus({ type: "success", message: response.data.message });
      setFormData({
        name: "",
        phone: "",
        email: "",
        address: "",
        service_type: "",
        message: ""
      });
    } catch (error) {
      setSubmitStatus({ 
        type: "error", 
        message: "Une erreur est survenue. Veuillez réessayer ou nous appeler directement." 
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-20 lg:py-32 bg-slate-50" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
          <div>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold uppercase tracking-tight mb-6" style={{ color: COLORS.bleu }} data-testid="contact-title">
              Demandez Votre<br />
              <span style={{ color: COLORS.rouge }}>Devis Gratuit</span>
            </h2>
            <p className="text-lg text-slate-600 mb-8">
              Remplissez le formulaire ci-contre et nous vous recontacterons dans les plus brefs délais 
              pour établir un devis personnalisé et gratuit.
            </p>
            
            <div className="space-y-6">
              <div className="flex items-center gap-4 p-4 rounded-xl border-2 border-blue-100 hover:border-[#002654] transition-colors" data-testid="contact-phone-block">
                <div className="w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: COLORS.bleu }}>
                  <Phone className="w-6 h-6 text-white" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm text-slate-500 font-semibold uppercase tracking-wider">Téléphone Bureau</div>
                  <a href="tel:0219271334" className="font-bold text-2xl hover:text-[#CE1126] transition-colors block" style={{ color: COLORS.bleu }} data-testid="contact-tel-bureau">
                    02 19 27 13 34
                  </a>
                  <div className="text-sm text-slate-500 mt-2">Urgence chantier</div>
                  <a href="tel:0756253169" className="font-semibold text-lg hover:text-[#CE1126] transition-colors block" style={{ color: COLORS.bleu }} data-testid="contact-tel-chantier">
                    07 56 25 31 69
                  </a>
                </div>
              </div>

              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${COLORS.rouge}15` }}>
                  <Send className="w-6 h-6" style={{ color: COLORS.rouge }} />
                </div>
                <div>
                  <div className="text-sm text-slate-500">Email</div>
                  <a href="mailto:couverturesfrancaisesouest@gmail.com" className="font-semibold hover:underline" style={{ color: COLORS.bleu }}>couverturesfrancaisesouest@gmail.com</a>
                </div>
              </div>
              
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${COLORS.bleu}15` }}>
                  <MapPin className="w-6 h-6" style={{ color: COLORS.bleu }} />
                </div>
                <div>
                  <div className="text-sm text-slate-500">Adresse</div>
                  <div className="font-semibold" style={{ color: COLORS.bleu }}>1, rue du Guesclin - 44000 Nantes</div>
                </div>
              </div>

              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${COLORS.rouge}15` }}>
                  <MapPin className="w-6 h-6" style={{ color: COLORS.rouge }} />
                </div>
                <div>
                  <div className="text-sm text-slate-500">Zone d'intervention</div>
                  <div className="font-semibold" style={{ color: COLORS.bleu }}>Loire-Atlantique, Maine-et-Loire, Vendée</div>
                  <div className="text-sm text-slate-500 mt-2 italic">Nous intervenons également sur le littoral : La Baule, Pornic, Les Sables d'Olonne.</div>
                </div>
              </div>
              
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${COLORS.bleu}15` }}>
                  <Clock className="w-6 h-6" style={{ color: COLORS.bleu }} />
                </div>
                <div>
                  <div className="text-sm text-slate-500">Disponibilité</div>
                  <div className="font-semibold" style={{ color: COLORS.bleu }}>Lun - Sam : 8h - 19h</div>
                </div>
              </div>
              
              {/* Boutons Google */}
              <div className="pt-4 border-t border-slate-200 space-y-3">
                <a
                  href="https://maps.app.goo.gl/D58N7CwsFxbgREmB9"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 w-full p-4 bg-white rounded-xl border-2 border-slate-200 hover:border-blue-400 transition-all shadow-sm hover:shadow-md"
                  data-testid="google-maps-btn"
                >
                  <svg className="w-8 h-8 flex-shrink-0" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  <div className="text-left flex-1">
                    <div className="text-xs text-slate-500">Retrouvez-nous sur</div>
                    <div className="font-bold" style={{ color: COLORS.bleu }}>Google Maps</div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-slate-400" />
                </a>

                <a
                  href="https://maps.app.goo.gl/D58N7CwsFxbgREmB9"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 w-full p-4 rounded-xl text-white transition-all shadow-md hover:shadow-lg hover:scale-[1.02]"
                  style={{ background: `linear-gradient(135deg, ${COLORS.rouge} 0%, ${COLORS.bleu} 100%)` }}
                  data-testid="avis-google-btn"
                >
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                    <Star className="w-6 h-6 fill-yellow-400 text-yellow-400" />
                  </div>
                  <div className="text-left flex-1">
                    <div className="text-xs opacity-90">Vous êtes client satisfait ?</div>
                    <div className="font-bold uppercase text-sm">Laissez un avis Google</div>
                  </div>
                  <ChevronRight className="w-5 h-5" />
                </a>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-2xl shadow-xl p-8 border-t-4" style={{ borderTopColor: COLORS.rouge }} data-testid="contact-form-container">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium mb-2" style={{ color: COLORS.bleu }}>
                    Nom complet *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="form-input"
                    placeholder="Votre nom"
                    data-testid="input-name"
                  />
                </div>
                
                <div>
                  <label htmlFor="phone" className="block text-sm font-medium mb-2" style={{ color: COLORS.bleu }}>
                    Téléphone *
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    className="form-input"
                    placeholder="06 XX XX XX XX"
                    data-testid="input-phone"
                  />
                </div>
              </div>
              
              <div>
                <label htmlFor="email" className="block text-sm font-medium mb-2" style={{ color: COLORS.bleu }}>
                  Email
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="form-input"
                  placeholder="votre@email.com"
                  data-testid="input-email"
                />
              </div>
              
              <div>
                <label htmlFor="address" className="block text-sm font-medium mb-2" style={{ color: COLORS.bleu }}>
                  Adresse du chantier
                </label>
                <input
                  type="text"
                  id="address"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  className="form-input"
                  placeholder="Adresse complète"
                  data-testid="input-address"
                />
              </div>
              
              <div>
                <label htmlFor="service_type" className="block text-sm font-medium mb-2" style={{ color: COLORS.bleu }}>
                  Type de service *
                </label>
                <select
                  id="service_type"
                  name="service_type"
                  value={formData.service_type}
                  onChange={handleChange}
                  required
                  className="form-input"
                  data-testid="select-service"
                >
                  <option value="">Sélectionnez un service</option>
                  <option value="renovation">Rénovation de toiture</option>
                  <option value="reparation">Réparation / Dépannage</option>
                  <option value="charpente">Charpente</option>
                  <option value="zinguerie">Zinguerie / Gouttières</option>
                  <option value="demoussage">Démoussage / Nettoyage</option>
                  <option value="isolation">Isolation de toiture</option>
                  <option value="traitement-bois">Traitement des bois</option>
                  <option value="maconnerie">Maçonnerie</option>
                  <option value="urgence">Intervention urgente</option>
                  <option value="autre">Autre</option>
                </select>
              </div>
              
              <div>
                <label htmlFor="message" className="block text-sm font-medium mb-2" style={{ color: COLORS.bleu }}>
                  Décrivez votre projet
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows={4}
                  className="form-input resize-none"
                  placeholder="Décrivez brièvement vos besoins..."
                  data-testid="textarea-message"
                ></textarea>
              </div>
              
              {submitStatus && (
                <div 
                  className={`p-4 rounded-xl ${
                    submitStatus.type === "success" 
                      ? "bg-green-50 text-green-800 border border-green-200" 
                      : "bg-red-50 text-red-800 border border-red-200"
                  }`}
                  data-testid="submit-status"
                >
                  <div className="flex items-center gap-2">
                    {submitStatus.type === "success" ? (
                      <CheckCircle2 className="w-5 h-5" />
                    ) : (
                      <AlertTriangle className="w-5 h-5" />
                    )}
                    {submitStatus.message}
                  </div>
                </div>
              )}
              
              <button
                type="submit"
                disabled={isSubmitting}
                className="btn-primary w-full py-4 text-lg disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="submit-button"
              >
                {isSubmitting ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Envoi en cours...
                  </span>
                ) : (
                  <span className="flex items-center justify-center gap-2">
                    <Send className="w-5 h-5" />
                    Envoyer ma demande
                  </span>
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

// Video Slideshow Section (AI-generated images with cinematic animation)
const VideoSlideshowSection = () => {
  const slides = [
    {
      image: "/ai-images/chantier.png",
      title: "Un savoir-faire artisanal",
      subtitle: "Pose de tuiles traditionnelles"
    },
    {
      image: "/ai-images/ardoise.png",
      title: "Maîtrise de l'ardoise",
      subtitle: "Techniques ancestrales"
    },
    {
      image: "/ai-images/charpente.png",
      title: "Charpente sur mesure",
      subtitle: "Bois de qualité, travail précis"
    },
    {
      image: "/ai-images/isolation.png",
      title: "Isolation performante",
      subtitle: "Confort et économies d'énergie"
    },
    {
      image: "/ai-images/panorama.png",
      title: "Des rénovations d'exception",
      subtitle: "Dans tout l'Ouest de la France"
    },
    {
      image: "/ai-images/gouttiere.png",
      title: "Gouttières et évacuation",
      subtitle: "Protection durable de votre maison"
    },
    {
      image: "/ai-images/zinc.png",
      title: "Zinguerie de précision",
      subtitle: "Finitions haut de gamme"
    },
    {
      image: "/ai-images/equipe.png",
      title: "Une équipe expérimentée",
      subtitle: "À votre service"
    }
  ];

  const [currentSlide, setCurrentSlide] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 4000);
    return () => clearInterval(timer);
  }, []);

  return (
    <section id="video" className="relative overflow-hidden bg-slate-900" data-testid="video-slideshow-section">
      <div className="relative h-[70vh] min-h-[500px] max-h-[700px] overflow-hidden">
        {slides.map((slide, index) => (
          <div
            key={index}
            className={`absolute inset-0 transition-all duration-1500 ease-in-out ${
              index === currentSlide ? "opacity-100 scale-100" : "opacity-0 scale-110"
            }`}
            style={{
              transitionDuration: "1500ms",
              transitionProperty: "opacity, transform"
            }}
          >
            <img
              src={slide.image}
              alt={slide.title}
              className="w-full h-full object-cover"
              style={{
                animation: index === currentSlide ? "kenBurns 6s ease-in-out infinite alternate" : "none"
              }}
            />
            <div className="absolute inset-0" style={{ background: `linear-gradient(to bottom, ${COLORS.bleu}66 0%, transparent 40%, ${COLORS.bleu}dd 100%)` }}></div>
          </div>
        ))}

        {/* Text overlay */}
        <div className="absolute inset-0 flex items-end pb-24 z-10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
            <div className="max-w-3xl">
              <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-4 py-2 mb-6">
                <div className="w-2 h-2 rounded-full animate-pulse" style={{ backgroundColor: COLORS.rouge }}></div>
                <span className="text-sm text-white font-medium uppercase tracking-wider">Notre savoir-faire</span>
              </div>
              {slides.map((slide, index) => (
                <div
                  key={index}
                  className={`transition-all duration-700 ${
                    index === currentSlide
                      ? "opacity-100 translate-y-0"
                      : "opacity-0 translate-y-8 absolute"
                  }`}
                >
                  <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-white uppercase tracking-tight leading-tight mb-3">
                    {slide.title}
                  </h2>
                  <p className="font-heading text-xl sm:text-2xl uppercase tracking-wide" style={{ color: COLORS.rouge }}>
                    {slide.subtitle}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Slide indicators */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex gap-3 z-20">
          {slides.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentSlide(index)}
              className={`h-1.5 rounded-full transition-all ${
                index === currentSlide ? "w-12" : "w-6 bg-white/40 hover:bg-white/60"
              }`}
              style={{
                backgroundColor: index === currentSlide ? COLORS.rouge : undefined
              }}
              data-testid={`slide-indicator-${index}`}
              aria-label={`Aller au slide ${index + 1}`}
            />
          ))}
        </div>

        {/* Play indicator (fake video feel) */}
        <div className="absolute top-8 right-8 z-20 flex items-center gap-2 bg-black/40 backdrop-blur-sm rounded-full px-4 py-2">
          <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse"></div>
          <span className="text-white text-xs font-semibold uppercase tracking-wider">Diaporama</span>
        </div>
      </div>

      <style>{`
        @keyframes kenBurns {
          0% { transform: scale(1); }
          100% { transform: scale(1.08); }
        }
      `}</style>
    </section>
  );
};

// Zones d'intervention - Local SEO
const ZonesSection = () => {
  const villes = [
    { nom: "Nantes", cp: "44000", slug: "nantes" },
    { nom: "Saint-Nazaire", cp: "44600", slug: "saint-nazaire" },
    { nom: "Rezé", cp: "44400", slug: "reze" },
    { nom: "Saint-Herblain", cp: "44800", slug: "saint-herblain" },
    { nom: "Vertou", cp: "44120", slug: "vertou" },
    { nom: "Clisson", cp: "44190", slug: "clisson" },
    { nom: "La Baule", cp: "44500", slug: "la-baule" },
    { nom: "Pornic", cp: "44210", slug: "pornic" },
    { nom: "La Roche-sur-Yon", cp: "85000", slug: "la-roche-sur-yon" },
    { nom: "Orvault", cp: "44700", slug: "orvault" },
    { nom: "Carquefou", cp: "44470", slug: "carquefou" },
    { nom: "Saint-Sébastien-sur-Loire", cp: "44230", slug: "saint-sebastien-sur-loire" },
    { nom: "Bouguenais", cp: "44340", slug: "bouguenais" },
    { nom: "La Chapelle-sur-Erdre", cp: "44240", slug: "la-chapelle-sur-erdre" },
    { nom: "Sainte-Luce-sur-Loire", cp: "44980", slug: "sainte-luce-sur-loire" },
  ];
  return (
    <section id="zones" className="py-20 bg-slate-50 scroll-mt-56" data-testid="zones-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10">
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold uppercase tracking-tight mb-4" style={{ color: COLORS.bleu }}>
            Nos zones d'intervention
          </h2>
          <div className="w-20 h-1 mx-auto mb-6" style={{ backgroundColor: COLORS.rouge }}></div>
          <p className="text-slate-600 text-lg max-w-2xl mx-auto">
            Couvreur en Loire-Atlantique — Découvrez nos pages dédiées par ville pour en savoir plus sur notre intervention locale.
          </p>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {villes.map((v) => (
            <a
              key={v.slug}
              href={`/couvreur-${v.slug}.html`}
              className="group bg-white rounded-xl p-5 border-2 border-slate-200 hover:border-[#CE1126] transition-all hover:shadow-lg flex flex-col items-center text-center"
              data-testid={`zone-${v.slug}`}
            >
              <MapPin className="w-6 h-6 mb-2" style={{ color: COLORS.rouge }} />
              <div className="font-heading font-bold text-lg uppercase" style={{ color: COLORS.bleu }}>
                {v.nom}
              </div>
              <div className="text-sm text-slate-500 mb-2">{v.cp}</div>
              <div className="text-xs font-semibold group-hover:text-[#CE1126]" style={{ color: COLORS.bleu }}>
                Couvreur {v.nom} →
              </div>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
};

// Footer Section
const Footer = () => {
  return (
    <footer style={{ backgroundColor: COLORS.bleu }} data-testid="footer">
      <TricolorBar />
      <div className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Bloc contact très visible */}
          <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-6 mb-10" data-testid="footer-contact-block">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-center text-center md:text-left">
              <div className="flex items-center justify-center md:justify-start gap-3">
                <div className="w-12 h-12 rounded-xl bg-white flex items-center justify-center flex-shrink-0">
                  <Phone className="w-6 h-6" style={{ color: COLORS.bleu }} />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wider text-blue-200">Bureau</div>
                  <a href="tel:0219271334" className="text-white font-bold text-xl hover:text-[#FFD700] transition-colors" data-testid="footer-tel-bureau">
                    02 19 27 13 34
                  </a>
                </div>
              </div>

              <div className="flex items-center justify-center md:justify-start gap-3">
                <div className="w-12 h-12 rounded-xl bg-[#CE1126] flex items-center justify-center flex-shrink-0">
                  <Phone className="w-6 h-6 text-white" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wider text-blue-200">Urgence chantier</div>
                  <a href="tel:0756253169" className="text-white font-bold text-xl hover:text-[#FFD700] transition-colors" data-testid="footer-tel-chantier">
                    07 56 25 31 69
                  </a>
                </div>
              </div>

              <div className="flex items-center justify-center md:justify-start gap-3">
                <div className="w-12 h-12 rounded-xl bg-white flex items-center justify-center flex-shrink-0">
                  <Send className="w-6 h-6" style={{ color: COLORS.bleu }} />
                </div>
                <div className="min-w-0">
                  <div className="text-xs uppercase tracking-wider text-blue-200">Email</div>
                  <a href="mailto:couverturesfrancaisesouest@gmail.com" className="text-white font-semibold hover:text-[#FFD700] transition-colors break-all" data-testid="footer-email">
                    couverturesfrancaisesouest@gmail.com
                  </a>
                </div>
              </div>
            </div>
          </div>

          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex flex-col items-center md:items-start gap-2">
              <img 
                src="https://customer-assets.emergentagent.com/job_build-website-77/artifacts/o6lgmf0g_IMG_1599.png" 
                alt="Couvertures Françaises de l'Ouest"
                className="h-20 w-auto object-contain"
              />
              <div className="text-center md:text-left">
                <span className="font-heading text-lg font-bold uppercase text-white">
                  Couvertures Françaises de l'Ouest
                </span>
                <div className="text-blue-200 text-sm mt-1">1, rue du Guesclin - 44000 Nantes</div>
              </div>
            </div>
          
            <div className="flex flex-wrap justify-center gap-6 text-white">
              <a href="#services" className="hover:text-[#FFD700] transition-colors">Services</a>
              <a href="#avantages" className="hover:text-[#FFD700] transition-colors">Avantages</a>
              <a href="#zones" className="hover:text-[#FFD700] transition-colors">Zones d'intervention</a>
              <a href="/blog.html" className="hover:text-[#FFD700] transition-colors">Blog</a>
              <a href="#temoignages" className="hover:text-[#FFD700] transition-colors">Témoignages</a>
              <a href="#contact" className="hover:text-[#FFD700] transition-colors">Contact</a>
            </div>
          
            <div className="text-blue-200 text-sm text-center">
              © 2026 Couvertures Françaises de l'Ouest<br/>Tous droits réservés
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

// Main App Component
function App() {
  return (
    <div className="App" data-testid="app">
      <Navigation />
      <HeroSection />
      <ServicesSection />
      <VideoSlideshowSection />
      <RealisationsSection />
      <BenefitsSection />
      <TestimonialsSection />
      <ContactSection />
      <ZonesSection />
      <Footer />
      
      {/* Bouton Urgence flottant (rouge, en haut) */}
      <a 
        href="tel:0756253169" 
        className="fixed bottom-20 right-4 z-50 flex items-center gap-2 text-white font-bold py-3 px-4 rounded-full shadow-lg hover:scale-105 transition-transform animate-pulse"
        style={{ backgroundColor: COLORS.rouge }}
        data-testid="urgence-btn"
      >
        <AlertTriangle className="w-5 h-5" />
        <span>URGENCE</span>
        <Phone className="w-5 h-5" />
      </a>

      {/* Bouton Appeler le Bureau flottant (bleu, en bas) */}
      <a 
        href="tel:0219271334" 
        className="fixed bottom-4 right-4 z-50 flex items-center gap-2 text-white font-bold py-3 px-4 rounded-full shadow-lg hover:scale-105 transition-transform"
        style={{ backgroundColor: COLORS.bleu }}
        data-testid="bureau-btn"
      >
        <Phone className="w-5 h-5" />
        <span>BUREAU</span>
      </a>
    </div>
  );
}

export default App;
